import { FC, useState } from 'react';
import { LazyMotion, domAnimation, m } from 'framer-motion';

import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import MobileToolTab from './mobile-tool-tab';
import { BREAKPOINTS } from '@lib/constants';
import getWindowDimensions from '@hooks/useWindowSize';
import CaptaincyPoll from '@components/CaptaincyPoll';
import FFHModal from '@components/modal';
import { H2 } from '@components/atoms/typography';
import { MiniFixtureTicker } from '@components/fixtures/v2';
import { useLeagueContext } from '@hooks/useLeagueContext';
import { PostHogFeatureFlags } from '@lib/posthog-feature-flags';
import { usePostHogFeatureFlag } from '@hooks/usePostHogFeatureFlag';
import { useCaptaincyPollDefinition } from '@hooks/useCaptaincyPoll';
const RightSidebar = dynamic(() => import('@components/layout/sidebar/right'));

type LayoutType = {
  hasRightSidebar?: boolean;
  content?: boolean;
  children: React.ReactNode;
};

const Layout: FC<LayoutType> = ({
  hasRightSidebar = true,
  children,
  content = true
}) => {
  const { context: leagueContext } = useLeagueContext();
  const currentGameweek = leagueContext?.currentGameweek?.number ?? 0;
  const router = useRouter();
  const [toolsModalIsOpen, setToolsModalIsOpen] = useState(false);
  const [toolToDisplay, setToolToDisplay] = useState<'poll' | 'fixture-ticker'>(
    'fixture-ticker'
  );

  const fplSeasonIsClosed = usePostHogFeatureFlag(
    PostHogFeatureFlags.fplSeasonIsClosed,
    false
  );

  const showMobileTools =
    getWindowDimensions().width < BREAKPOINTS.lg &&
    router.pathname === '/' &&
    !fplSeasonIsClosed &&
    currentGameweek !== 38;

  const communityVoteIsLive = usePostHogFeatureFlag(
    PostHogFeatureFlags.communityVoteIsLive,
    false
  );

  const { status: captaincyPollStatus } = useCaptaincyPollDefinition({
    isEnabled: showMobileTools && communityVoteIsLive
  });

  return (
    <main>
      <LazyMotion features={domAnimation}>
        {/* @ts-ignore */}
        <m.div
          exit={{ opacity: 0 }}
          initial={{ opacity: 0.1 }}
          animate={{ opacity: 1 }}
        >
          <div className="container mx-auto px-3">
            <div
              className={`flex flex-col lg:grid ${
                hasRightSidebar
                  ? 'lg:grid-cols-[minmax(0,1fr)_300px] xl:grid-cols-[minmax(0,1fr)_325px]'
                  : ''
              } gap-8`}
            >
              <div>
                {showMobileTools && (
                  <div>
                    <H2 className="mt-5 mb-3">Community & tools</H2>
                    <div className="space-y-3">
                      {communityVoteIsLive &&
                        captaincyPollStatus !== 'hidden' && (
                          <MobileToolTab
                            title="🗳️ Captaincy poll"
                            subtitle={
                              captaincyPollStatus === 'open'
                                ? 'Who are you captaining this week?'
                                : 'See who everyone captained'
                            }
                            onClick={() => {
                              setToolsModalIsOpen(true);
                              setToolToDisplay('poll');
                            }}
                          />
                        )}
                      <MobileToolTab
                        title="🔍 Fixture ticker"
                        subtitle="See a complete list of all upcoming fixtures"
                        onClick={() => {
                          setToolsModalIsOpen(true);
                          setToolToDisplay('fixture-ticker');
                        }}
                      />
                    </div>
                  </div>
                )}
                <section className={`${content ? 'content' : ''}`}>
                  {children}
                </section>
              </div>
              {hasRightSidebar && (
                <div className="hidden lg:block">
                  <RightSidebar />
                </div>
              )}
            </div>
          </div>
        </m.div>
      </LazyMotion>
      <FFHModal
        open={toolsModalIsOpen}
        handleClose={() => setToolsModalIsOpen(false)}
        childrenClassName="w-75vw"
      >
        {toolToDisplay === 'poll' && (
          <div onClick={(event) => event.stopPropagation()}>
            <CaptaincyPoll placement="mobile_modal" />
          </div>
        )}
        {toolToDisplay === 'fixture-ticker' && (
          <div onClick={(e) => e.stopPropagation()}>
            <MiniFixtureTicker />
          </div>
        )}
      </FFHModal>
    </main>
  );
};

export default Layout;
